import React from 'react';
import {Link} from 'react-router-dom';
import {UiuxLvlLife} from '../../components/UIUX/uiux';
import workImg from '../../assets/images/lvl/workout.png';
import habitImg from '../../assets/images/lvl/pastHabit.png';
import systemImg from '../../assets/images/lvl/system.png';


const lvlInfo = {
  caseStudy: {
    overview: "Lvl Life is a simple productivity and fitness web app designed to help users track their workouts, habits, and daily goals in a clear, minimalist interface. It began as a personal project to help my friends and I stay accountable with our physical and personal growth routines, and evolved into a concept that could serve anyone looking to build consistent habits while staying mentally and physically balanced.",
    persona: {
      user1: {
        name: "Jordan M.",
        age: 27,
        occupation: "Warehouse Supervisor",
        painPoint: "Wants to lose weight and stay consistent with physical rehab exercises, but struggles to track daily progress.",
        needs: "A no-friction app that helps him log workouts, add notes, and reflect on progress without being overwhelming."
      },
      user2: {
        name: "Tasha W.",
        age: 29,
        occupation: "Freelance Illustrator",
        painPoint: "Struggles with maintaining creative and personal habits like meditation, reading, and fitness while juggling gigs.",
        needs: "A daily habit tracker that shows past performance and helps her feel accomplished and focused."
      },
    },
    problem: {
      main: "Many habit and fitness tracking apps are either bloated with unnecessary features or hyper-focused on one aspect of wellness. There wasn’t a tool that let my friends and I:",
      points: ["Track workouts and habits in one simple flow", "Mark progress as complete / partial / incomplete", "Add notes for context or reflection", "View past performance easily", "Encourage each other socially (future feature)"]
    },
    solution: {
      main: "I designed Lvl Life to be a unified space for tracking:",
      points: ["Daily workouts categorized by body focus (e.g., Cardio & Legs, Arms, Core)", "Daily habits like meditation, reading, and guitar", "Completion statuses with visual indicators (green = done, orange = partial, white = incomplete)", "Personal notes added to each item for journaling, context, or adjustments", "Progress views and calendar-based habit history"]
    },
    process: {
      wireframe: ["Sketched out quick flows for a user landing on the app and selecting a workout.", "Prioritized simplicity over customization — the key was frictionless tracking."],
      visual: ["Color-Coded Progress: Each task has a simple color-coded status circle to indicate its completion level", "Minimalist Interface: Inspired by paper planners — large text, generous spacing, and soft color blocks.", "Consistency: Each section uses the same card layout and color logic for ease of use."],
      feedback: ["Tested static designs with friends (the original inspiration).", "They appreciated the visual simplicity and requested notes + calendar views, which I integrated."]
    },
    screens: {
      home: ["Choose from Cardio & Legs, Arms, or Core.", "Quick access to View Progress / Calendar.", "Option to view the Habits, Workouts, and Goals pages persist at the bottom of screen for easy access."],
      workout: ["Task list with completion status + note fields."],
      habit: ["Daily habit tracking with a date-based history.", "Notes and visual statuses mirror the workout section for UX consistency."],
      goals: ["Notes and visual statuses mirror the other sections for UX consistency."],
      calendar: ["A quick overview of all your progress & history across all tracked options (Goals, Workouts, and Daily Habits)."]
    },
    steps: ["Social Features: Add friend syncing so users can view and encourage each other.", "Streaks & Rewards: Encourage consistency without pressure.", "Deploy the web app publicly and gather real user feedback.", "Mobile-first design optimization to ensure perfect responsiveness."],
    reflection: "Designing Lvl Life helped me grow as a product thinker — not just a designer. I had to balance UX clarity with real functionality, all while solving a personal pain point that many others share. Building it also gave me deeper understanding of how good design can reduce friction and support consistency in people’s lives.",
    tools: ["Figma (UI/UX design)", "React / Next.js (development in progress)", "User feedback and iterative design"],
  }
}


const lvlLifePage = () => {
  return (
    <div className='uiux-p-wrapper'>
        <h1 className='uiux-p-header'>UI/UX - Lvl Life</h1>
        <div className='uiux-p-single'>
          <Link to="/uiux" className="fa-solid fa-arrow-left uiux-p-single-back" key="back"></Link>
          <a href="https://github.com/Makkiah/Lvl-Life" className='uiux-p-single-github' target='_blank' rel="noreferrer" aria-label="Visit LVL Life GitHub" title="LVL-Life Repository">
            View LVL-Life Repository on Github
          </a>
          <h3 className='uiux-p-single-title'>Overview</h3>
          <p className="uiux-p-single-overview">{lvlInfo.caseStudy.overview}</p>
          <h3 className='uiux-p-single-title' style={{marginTop: "20px"}}>Personas</h3>
          <div className='uiux-p-single-div'>
            <div className='uiux-p-single-div-side'>
              <h4 className="uiux-p-single-title">{lvlInfo.caseStudy.persona.user1.name} - {lvlInfo.caseStudy.persona.user1.age}</h4>
              <p className="uiux-p-single-text"><span className="uiux-p-single-text-span" >Occupation: </span>{lvlInfo.caseStudy.persona.user1.occupation}</p>
              <p className="uiux-p-single-text"><span className="uiux-p-single-text-span" >Pain Point: </span>{lvlInfo.caseStudy.persona.user1.painPoint}</p>
              <p className="uiux-p-single-text"><span className="uiux-p-single-text-span" >Needs: </span>{lvlInfo.caseStudy.persona.user1.needs}</p>
            </div>
            <div className='uiux-p-single-div-side'>
              <h4 className="uiux-p-single-title">{lvlInfo.caseStudy.persona.user2.name} - {lvlInfo.caseStudy.persona.user2.age}</h4>
              <p className="uiux-p-single-text"><span className="uiux-p-single-text-span" >Occupation: </span>{lvlInfo.caseStudy.persona.user2.occupation}</p>
              <p className="uiux-p-single-text"><span className="uiux-p-single-text-span" >Pain Point: </span>{lvlInfo.caseStudy.persona.user2.painPoint}</p>
              <p className="uiux-p-single-text"><span className="uiux-p-single-text-span" >Needs: </span>{lvlInfo.caseStudy.persona.user2.needs}</p>
            </div>
          </div>
          <div className='uiux-p-single-div2'>
            <div className='uiux-p-single-div2-side'>
              <img src={workImg} alt="Workout screen" className='uiux-p-single-div3-side-img'/>
            </div>
            <div className='uiux-p-single-div2-side'>
              <p className="uiux-p-single-text"><span className="uiux-p-single-text-span-title" >The Problem: </span>{lvlInfo.caseStudy.problem.main}</p>
              <ul className='uiux-p-single-ul'>
                {
                  lvlInfo.caseStudy.problem.points.map((point, index) => {
                    return (
                      <li className='uiux-p-single-ul-li' key={index}>{point}</li>
                    )
                  })
                }
              </ul>
            </div>
          </div>
          <div className='uiux-p-single-div3'>
            <div className='uiux-p-single-div3-side'>
              <p className="uiux-p-single-text"><span className="uiux-p-single-text-span-title" >The Solution: </span>{lvlInfo.caseStudy.solution.main}</p>
              <ul className='uiux-p-single-ul'>
                {
                  lvlInfo.caseStudy.solution.points.map((point, index) => {
                    return (
                      <li className='uiux-p-single-ul-li' key={index}>{point}</li>
                    )
                  })
                }
              </ul>
            </div>
            <div className='uiux-p-single-div3-side'>
              <img src={habitImg} alt="Past Habits Completed" className='uiux-p-single-div3-side-img'/>
            </div>
          </div>
          <div className='uiux-p-single-div4'>
            <h3 className='uiux-p-single-title'>Design Process</h3> 
            <p className='uiux-p-single-title-sub'>1. Wireframes & User Flow Mapping</p>
            <ul className='uiux-p-single-ul'>
              {
                lvlInfo.caseStudy.process.wireframe.map((point, index) => {
                  return (
                    <li className='uiux-p-single-ul-li' key={index}>{point}</li>
                  )
                })
              }
            </ul>
            <p className='uiux-p-single-title-sub'>2. Visual Design</p>
            <ul className='uiux-p-single-ul'>
              {
                lvlInfo.caseStudy.process.visual.map((point, index) => {
                  return (
                    <li className='uiux-p-single-ul-li' key={index}>{point}</li>
                  )
                })
              }
            </ul>
            <p className='uiux-p-single-title-sub'>3. Prototype and Feedback</p>
            <ul className='uiux-p-single-ul' style={{marginBottom: "0px"}}>
              {
                lvlInfo.caseStudy.process.feedback.map((point, index) => {
                  return (
                    <li className='uiux-p-single-ul-li' key={index}>{point}</li>
                  )
                })
              }
            </ul>
          </div>
          <img src={systemImg} alt="Workout screen" className='uiux-p-single-img'/>
          <h3 className='uiux-p-single-title' style={{margin: "20px 0"}}>Key Screens</h3> 
          <div className='uiux-p-single-div5'>
            <div className='uiux-p-single-div5-side'>
              <p className="uiux-p-single-text-span-title">Home:</p>
              <ul className='uiux-p-single-ul'>
                {
                  lvlInfo.caseStudy.screens.home.map((point, index) => {
                    return (
                      <li className='uiux-p-single-ul-li' key={index}>{point}</li>
                    )
                  })
                }
              </ul>
            </div>
            <div className='uiux-p-single-div5-side'>
              <p className="uiux-p-single-text-span-title">Workout:</p>
              <ul className='uiux-p-single-ul'>
                {
                  lvlInfo.caseStudy.screens.workout.map((point, index) => {
                    return (
                      <li className='uiux-p-single-ul-li' key={index}>{point}</li>
                    )
                  })
                }
              </ul>
            </div>
            <div className='uiux-p-single-div5-side'>
              <p className="uiux-p-single-text-span-title">Habits:</p>
              <ul className='uiux-p-single-ul'>
                {
                  lvlInfo.caseStudy.screens.habit.map((point, index) => {
                    return (
                      <li className='uiux-p-single-ul-li' key={index}>{point}</li>
                    )
                  })
                }
              </ul>
            </div>
            <div className='uiux-p-single-div5-side'>
              <p className="uiux-p-single-text-span-title">Goals:</p>
              <ul className='uiux-p-single-ul'>
                {
                  lvlInfo.caseStudy.screens.goals.map((point, index) => {
                    return (
                      <li className='uiux-p-single-ul-li' key={index}>{point}</li>
                    )
                  })
                }
              </ul>
            </div>
            <div className='uiux-p-single-div5-side'>
              <p className="uiux-p-single-text-span-title">Calendar:</p>
              <ul className='uiux-p-single-ul'>
                {
                  lvlInfo.caseStudy.screens.calendar.map((point, index) => {
                    return (
                      <li className='uiux-p-single-ul-li' key={index}>{point}</li>
                    )
                  })
                }
              </ul>
            </div>
          </div>
          <p className='uiux-p-single-subtext'>View Figma designs by clicking the dropdown menu in the top-left corner below.</p>
          <br/>
          < UiuxLvlLife />
          <h3 className='uiux-p-single-title' style={{marginTop: "20px"}}>Next Steps</h3> 
          <ul className='uiux-p-single-ul'>
            {
              lvlInfo.caseStudy.steps.map((point, index) => {
                return (
                  <li className='uiux-p-single-ul-li' key={index}>{point}</li>
                )
              })
            }
          </ul>
          <h3 className='uiux-p-single-title'>Reflection</h3> 
          <p className='uiux-p-single-text'>{lvlInfo.caseStudy.reflection}</p>
          <h3 className='uiux-p-single-title' style={{marginTop: "20px"}}>Tools Used</h3> 
          <ul className='uiux-p-single-ul'>
            {
              lvlInfo.caseStudy.tools.map((point, index) => {
                return (
                  <li className='uiux-p-single-ul-li' key={index}>{point}</li>
                )
              })
            }
          </ul>
        </div>
    </div>
  )
}

export default lvlLifePage